import { useEffect, useCallback, useState } from "react";

import { useDroppable } from "@dnd-kit/core";

import { useFileDndContext } from "./FileDndContext";

import type { DropData } from "./FileDndContext";
import type { Active } from "@dnd-kit/core";

type UseFileDroppableReturn = {
  setNodeRef: (node: HTMLElement | null) => void;
  isOver: boolean;
  active: Active | null;
  isFileDragging: boolean;
};

export function useFileDroppable<T extends DropData = DropData>({
  id,
  disabled,
  data,
}: {
  id: string;
  disabled?: boolean;
  data: T;
}): UseFileDroppableReturn {
  const context = useFileDndContext();
  const [isFileOver, setIsFileOver] = useState(false);

  const {
    isFileDragging,
    registerDroppable,
    unregisterDroppable,
    setFileDropOver,
    resetFileDropOver,
  } = context;

  const {
    isOver: dndKitIsOver,
    setNodeRef: dndKitSetNodeRef,
    active,
  } = useDroppable({
    id,
    data,
  });

  const [nodeRef, setNodeRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!disabled) {
      registerDroppable(id, data);
    }
    return () => {
      if (!disabled) {
        unregisterDroppable(id);
      }
    };
  }, [id, data, disabled, registerDroppable, unregisterDroppable]);

  // Combined ref that sets both dnd-kit ref and native event handlers
  const setCombinedNodeRef = useCallback(
    (node: HTMLElement | null) => {
      if (disabled) return;
      setNodeRef(node);
      dndKitSetNodeRef(node);
    },
    [dndKitSetNodeRef, disabled],
  );

  useEffect(() => {
    if (!nodeRef) {
      return;
    }

    const handleFileDragEnter = () => {
      if (!isFileDragging || disabled) {
        return;
      }
      setFileDropOver(id);
      setIsFileOver(true);
    };

    const handleFileDragLeave = (e: DragEvent) => {
      if (!isFileDragging || disabled) {
        return;
      }
      if (
        e.relatedTarget instanceof HTMLElement &&
        e.currentTarget instanceof HTMLElement &&
        e.currentTarget.contains(e.relatedTarget)
      )
        return;

      resetFileDropOver(id);
      setIsFileOver(false);
    };

    nodeRef.addEventListener("dragenter", handleFileDragEnter);
    nodeRef.addEventListener("dragleave", handleFileDragLeave);

    return () => {
      nodeRef.removeEventListener("dragenter", handleFileDragEnter);
      nodeRef.removeEventListener("dragleave", handleFileDragLeave);
    };
  }, [nodeRef, isFileDragging, id, disabled, setFileDropOver, resetFileDropOver]);

  useEffect(() => {
    if (!isFileDragging) {
      setIsFileOver(false);
    }
  }, [isFileDragging]);

  const isOver = dndKitIsOver || isFileOver;

  return {
    setNodeRef: setCombinedNodeRef,
    isOver,
    active,
    isFileDragging,
  };
}
